/**
 * The dashboard's HTTP GraphQL read client (`@authe/fe-commons/graphql`).
 *
 * This is the "fast path" the transport registry (`@authe/fe-commons/transport`)
 * routes composed reads to (catalog lists, address overlays, the recent feed).
 * It is SEPARATE from the streaming client in `./streaming` (that owns the
 * realtime WebSocket subprotocol); this one is a plain POST for one-shot reads.
 *
 * Auth mirrors the streaming/publish path: a fresh bearer is pulled per request
 * from the `SessionManager` and rides the `Authorization` header verbatim (the
 * private-api AppSync authorizer reads it exactly like the streaming hub).
 *
 * The endpoint REGION-SWITCHES like the REST client: the AppSync custom domain
 * is the REST `.private.` host with a `graphql.` label
 * (`https://graphql.{alias}.{continent}.private.{domain}/graphql`), so it's
 * resolved PER REQUEST from the same JWT + shared `regionRoutingConfig` the
 * `ApiClient` uses — region alias, continent, the `_use_region_alias` pin and
 * minute failover all move REST and GraphQL together. `PRIVATE_API_GRAPHQL_URL`
 * (the AppSync-managed URL) stays as the pre-token fallback base, exactly like
 * `ApiClient.baseUrl`.
 *
 * `createDashboardGraphqlClient` returns `null` when no endpoint is configured
 * (`PRIVATE_API_GRAPHQL_URL` unset) — in which case `resolveTransport` keeps
 * every operation on REST and the whole GraphQL layer is a no-op.
 */
import * as React from "react";
import {
  deploymentColourForService,
  resolveRegion,
  rewriteRegionGraphqlUrl,
} from "@authe/fe-commons/api";
import { GraphQLClient } from "@authe/fe-commons/graphql";
import { appConfig } from "@/config";
import { regionRoutingConfig } from "./routing-config";

/**
 * QA-config service key for the private-api backend. The GraphQL read layer is
 * served by private-api, so its blue/green colour follows the SAME per-service
 * `qa_config` pin the REST private-api calls use — keep this in lockstep with
 * the service name the QA portal / blue-users config writes.
 */
const PRIVATE_API_SERVICE = "private-api";

/**
 * Resolve the GraphQL endpoint for THIS request: the caller's regional AppSync
 * custom domain once the JWT lands, else the static fallback base. Returns
 * `null` only when neither is available (transport then falls back to REST).
 *
 * The colour host (green `graphql.*` vs blue `graphql-blue.*`) is picked from the
 * caller's `qa_config` for `private-api` — AppSync has no `/blue/*` path, so
 * blue/green is a separate endpoint. Defaults to green when unpinned.
 */
function resolveGraphqlEndpoint(): string | null {
  const token = regionRoutingConfig.getToken();
  if (token) {
    const resolved = resolveRegion(token, regionRoutingConfig);
    const colour = deploymentColourForService(token, regionRoutingConfig, PRIVATE_API_SERVICE);
    const regional = rewriteRegionGraphqlUrl(token, regionRoutingConfig, resolved, colour);
    if (regional) return regional;
  }
  return appConfig.privateApiGraphqlUrl ?? null;
}

/**
 * Warm DNS + TLS (and, on HTTP/2, the connection itself) to the resolved
 * GraphQL origin so the FIRST composed read doesn't pay the ~2-RTT handshake to
 * the AppSync custom domain. In the browser, keep-alive / HTTP-2 already reuses
 * that connection for every SUBSEQUENT read in the session — so this only
 * removes the one-time cold-connect cost (the "new TLS per call" penalty is a
 * non-browser-client problem, e.g. curl/Postman/a-remote-lambda, not the SPA).
 *
 * `crossorigin` matches the read's anonymous CORS mode (bearer header, no
 * cookies) so the pre-opened socket is actually reused rather than discarded.
 * Idempotent per origin; a no-op off-DOM (SSR / tests) or when GraphQL isn't
 * configured. Call once the region-resolving token snapshot is live (see
 * `providers.tsx`) so we warm the caller's REGIONAL host, not the fallback base.
 */
export function preconnectGraphqlEndpoint(): void {
  if (typeof document === "undefined") return;
  const endpoint = resolveGraphqlEndpoint();
  if (!endpoint) return;

  let origin: string;
  try {
    origin = new URL(endpoint).origin;
  } catch {
    return;
  }

  if (document.head.querySelector(`link[data-graphql-preconnect="${origin}"]`)) return;

  const preconnect = document.createElement("link");
  preconnect.rel = "preconnect";
  preconnect.href = origin;
  preconnect.crossOrigin = "anonymous";
  preconnect.setAttribute("data-graphql-preconnect", origin);
  document.head.appendChild(preconnect);

  // dns-prefetch fallback for engines that ignore `rel=preconnect`.
  const dns = document.createElement("link");
  dns.rel = "dns-prefetch";
  dns.href = origin;
  dns.setAttribute("data-graphql-preconnect", origin);
  document.head.appendChild(dns);
}

/** Build the read client, or `null` when the endpoint isn't provisioned. */
export function createDashboardGraphqlClient(
  getToken: () => Promise<string | null>,
): GraphQLClient | null {
  if (!appConfig.privateApiGraphqlUrl) return null;
  // NOTE: the request-key/anti-replay signer is no longer wired here —
  // `GraphQLClientConfig` (fe-commons) dropped the per-request signer hook, so
  // the GraphQL read path relies on the bearer token alone (the REST `ApiClient`
  // still signs via `requestSigner`). Re-add here if fe-commons reintroduces it.
  return new GraphQLClient({
    endpoint: resolveGraphqlEndpoint,
    getAccessToken: getToken,
  });
}

/** Whether the GraphQL read layer is configured (gates the transport registry). */
export const isGraphqlConfigured = Boolean(appConfig.privateApiGraphqlUrl);

const GraphqlClientContext = React.createContext<GraphQLClient | null>(null);

export interface GraphqlProviderProps {
  client: GraphQLClient | null;
  children: React.ReactNode;
}

/** Publish the read client (or `null`) to the tree. */
export function GraphqlProvider({ client, children }: GraphqlProviderProps): React.ReactElement {
  return <GraphqlClientContext.Provider value={client}>{children}</GraphqlClientContext.Provider>;
}

/**
 * The read client, or `null` when GraphQL isn't configured. Adapters that want
 * the fast path read this AND `resolveTransport(...)`; a `null` client (or a
 * "rest" verdict) means they use the REST `ApiClient`.
 */
export function useOptionalGraphqlClient(): GraphQLClient | null {
  return React.useContext(GraphqlClientContext);
}
